import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, Button, Card, message, Spin, Typography, Rate, DatePicker, Select, Checkbox, Radio, Upload, Slider, InputNumber, Row, Col } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function PublicFeedbackForm() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [linkData, setLinkData] = useState<any>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchMagicLinkData();
  }, [token]);

  const fetchMagicLinkData = async () => {
    try {
      const res = await fetch(`/api/v1/feedback/events/magic-links/${token}/validate`);
      if (!res.ok) {
        throw new Error('Invalid or expired link');
      }
      const data = await res.json();
      setLinkData(data);
    } catch (error) {
      message.error('This link is invalid or has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const res = await fetch(`/api/v1/feedback/events/magic-links/${token}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        message.error('Failed to submit feedback');
      }
    } catch (error) {
      message.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormField = (field: any) => {
    const isRequired = field.required;
    const rules = isRequired ? [{ required: true, message: `${field.label} is required` }] : [];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <Form.Item
            key={field.id}
            name={field.id}
            label={field.label}
            rules={rules}
          >
            <Input placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <TextArea rows={4} placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'password':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Input.Password placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'number':
      case 'decimal':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <InputNumber style={{ width: '100%' }} placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'currency':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <InputNumber style={{ width: '100%' }} prefix="$" placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        );

      case 'datetime':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        );

      case 'select':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Select placeholder={field.placeholder}>
              {field.options?.map((opt: string, idx: number) => (
                <Select.Option key={idx} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        );

      case 'multiselect':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Select mode="multiple" placeholder={field.placeholder}>
              {field.options?.map((opt: string, idx: number) => (
                <Select.Option key={idx} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        );

      case 'radio':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Radio.Group>
              {field.options?.map((opt: string, idx: number) => (
                <Radio key={idx} value={opt}>{opt}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        );

      case 'checkbox':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Checkbox.Group options={field.options} />
          </Form.Item>
        );

      case 'rating':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Rate />
          </Form.Item>
        );

      case 'slider':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Slider />
          </Form.Item>
        );

      case 'file':
      case 'image':
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Upload>
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Form.Item>
        );

      case 'heading':
        return <Title key={field.id} level={4} style={{ marginTop: 16 }}>{field.label}</Title>;

      case 'divider':
        return <div key={field.id} style={{ borderTop: '1px solid #e8e8e8', margin: '16px 0' }} />;

      default:
        return (
          <Form.Item key={field.id} name={field.id} label={field.label} rules={rules}>
            <Input placeholder={field.placeholder} />
          </Form.Item>
        );
    }
  };

  const renderFormGrid = () => {
    if (!linkData?.form) return null;

    const { fields, columns } = linkData.form.layout;
    
    // Organize fields into rows
    const rows: any[][] = [];
    const rowMap = new Map<number, any[]>();

    fields.forEach((field: any) => {
      const row = field.position.row;
      if (!rowMap.has(row)) rowMap.set(row, []);
      rowMap.get(row)!.push(field);
    });

    const sortedRows = Array.from(rowMap.keys()).sort((a, b) => a - b);
    sortedRows.forEach((rowNum) => {
      rows.push(rowMap.get(rowNum)!.sort((a, b) => a.position.col - b.position.col));
    });

    return rows.map((row, rowIndex) => (
      <Row gutter={16} key={rowIndex}>
        {row.map((field) => {
          const span = ((field.position.colSpan || 1) / columns) * 24;
          return (
            <Col span={span} key={field.id}>
              {renderFormField(field)}
            </Col>
          );
        })}
      </Row>
    ));
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!linkData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card style={{ maxWidth: 500, textAlign: 'center' }}>
          <Title level={3}>Invalid Link</Title>
          <Text type="secondary">This feedback link is invalid or has expired.</Text>
        </Card>
      </div>
    );
  }

  // Show success screen after submission
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card style={{ maxWidth: 600, textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 72, color: '#52c41a', marginBottom: 24, fontWeight: 'bold' }}>
            ✓
          </div>
          <Title level={2}>Thank You!</Title>
          <Text style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>
            Your feedback has been submitted successfully.
          </Text>
          <Text type="secondary" style={{ display: 'block' }}>
            We appreciate you taking the time to share your thoughts with us.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: '40px 20px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <Card>
          <Title level={2}>{linkData.event?.name || 'Feedback Form'}</Title>
          {linkData.event?.description && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              {linkData.event.description}
            </Text>
          )}

          {linkData.form ? (
            <>
              <Title level={4}>{linkData.form.name}</Title>
              {linkData.form.description && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                  {linkData.form.description}
                </Text>
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
              >
                {renderFormGrid()}

                <Form.Item style={{ marginTop: 24 }}>
                  <Button type="primary" htmlType="submit" size="large" loading={submitting} block>
                    Submit Feedback
                  </Button>
                </Form.Item>
              </Form>
            </>
          ) : (
            <Text type="secondary">No form configured for this event.</Text>
          )}
        </Card>
      </div>
    </div>
  );
}
